import { type FC, forwardRef, useMemo, useState } from "react";
import { usePress } from "react-aria";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import {
  Icons,
  Item,
  MenuOptionItem,
  MenuOptionItemIntent,
  type MenuOptionItemProps,
  MenuTriggerButton,
  Section,
  TriggerButtonSize,
} from "widgets";
import { getFilenameInfo } from "widgets/utils";

import { BrowserTab } from "@/app-utils/browser-tab.ts";
import type { Configuration, ProjectFile } from "@/graphql.ts";
import { TileStatus } from "@/modules/core/components/settings.ts";
import { Tile, type TileProps } from "@/modules/core/components/Tile.tsx";
import { FilePreviewControlledDialog } from "@/modules/files/components/FilePreviewControlledDialog.js";
import {
  getParentPath,
  isAlignmentFile,
  isAnalysisSetFile,
  isFastaFile,
  isGenomeViewerFile,
} from "@/modules/files/utils/file.js";
import { notify } from "@/modules/notifications/notify.tsx";
import { isClassicUiDisabled } from "@/redux/modules/config.ts";
import {
  downloadJnlpFile,
  getGenomeViewerMenuItemDisabledHint,
  getProjectFilesDataForSendingToGenomeViewer,
} from "@/redux/modules/files.ts";
import { forwardToAnalyzer } from "@/redux/modules/table-data.ts";
import type { IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import {
  ANALYZERS,
  CONTENT_TYPES,
  ENTITY_TYPE,
  EVENT_RESOURCE_TYPES,
  EXT_APP_MODULES,
  FILE_BROWSE_TYPES,
  FILE_DISPLAY_NAME,
} from "@/settings/settings.ts";
import BamFileValidationDialog from "@/ui-modules/files/BamFileValidationDialog.tsx";
import { FileDetailsDialog } from "@/ui-modules/files/FileDetailsDialog.js";

import { FileIcon } from "../../../ui-modules/core/FileIcon.js";
import { checkProjectFileExists, downloadProjectFile } from "@selector/shared/jsx";
import { selMessage } from "@selector/shared";

/**
 * File Tile
 * - Generic file type label is automatic
 */
interface FileTileProps extends Pick<
  TileProps,
  "onPress" | "tooltip" | "footNote" | "type" | "style" | "lightweight" | "forceExternal"
> {
  file: ProjectFile;
  hideIcon?: boolean;
  noPageChangeActions?: boolean;
}

// TODO File Info Dialog ?

export const FileTile = forwardRef<HTMLDivElement, FileTileProps>(function FileTile(props, ref) {
  const file = props.file;
  const navigate = useNavigate();
  const dispatch = useDispatch<ReduxDispatch>();
  const configuration = useSelector<IAppState, Configuration>((state) => state.config.configuration, shallowEqual);

  const [projectFilesForGenomeViewer, setProjectFilesForGenomeViewer] = useState<ProjectFile[] | null>(null);
  const [previewOpened, setPreviewOpened] = useState<boolean>(false);
  const [infoOpened, setInfoOpened] = useState<boolean>(false);
  const genomeViewerMenuItemDisabledHint = getGenomeViewerMenuItemDisabledHint(file);

  const isFolder =
    (file.fileBrowseType as FILE_BROWSE_TYPES) === FILE_BROWSE_TYPES.DIRECTORY ||
    file.contentType?.id === CONTENT_TYPES.DIRECTORY;

  const type = props.type === EVENT_RESOURCE_TYPES.UNKNOWN ? undefined : props.type;
  const fileType = isFolder
    ? FILE_DISPLAY_NAME[FILE_BROWSE_TYPES.DIRECTORY]
    : (getFilenameInfo(file.fileName)?.typeLabel ?? file.typeDisplayName ?? "");

  const Menu = useMemo(() => {
    const disabledMenuEntries: string[] = [];
    if (genomeViewerMenuItemDisabledHint) disabledMenuEntries.push("open-genome-viewer");

    const items: { id: string; items: MenuOptionItemProps[] }[] = [
      { id: "more", items: [{ id: "download", title: "Download", icon: "download" }] },
    ];

    if (
      (isGenomeViewerFile(file) ||
        isAnalysisSetFile(file) ||
        (!isClassicUiDisabled(configuration) && (isAlignmentFile(file) || isFastaFile(file)))) &&
      !props.noPageChangeActions
    ) {
      items.unshift({ id: "send-to", items: [] });

      if (isGenomeViewerFile(file)) {
        items[0].items.push({
          id: "open-genome-viewer",
          title: "Send to Genome Viewer",
          icon: <img src={Icons.Action.GenomeViewer} width={20} height={20} alt="" />,
          intent: MenuOptionItemIntent.Primary,
          description: genomeViewerMenuItemDisabledHint,
        });
      }

      if (isAnalysisSetFile(file)) {
        items[0].items.push(
          {
            id: "open-data-viewer",
            title: "Send to Analytical Data Viewer",
            icon: "bar_chart",
            intent: MenuOptionItemIntent.Primary,
          },
          {
            id: "open-analyst",
            title: "Send to Analyst",
            icon: <img src={Icons.Action.Analyst} width={20} height={20} alt="Analyst" />,
            intent: MenuOptionItemIntent.Primary,
          },
        );
      }

      if ((isAlignmentFile(file) || isFastaFile(file)) && !isClassicUiDisabled(configuration)) {
        items[0].items.push({
          id: "open-sequence-analyzer",
          title: `Send to ${ANALYZERS.SEQUENCE_ANALYZER}`,
          icon: <img src={Icons.Action.GenomeViewer} width={20} height={20} alt={ANALYZERS.SEQUENCE_ANALYZER} />,
          intent: MenuOptionItemIntent.Primary,
        });
      }
    }

    if (file.previewable) {
      (items[1] ?? items[0]).items.unshift({ id: "preview", title: "Preview", icon: "zoom_in" });
    }

    (items[1] ?? items[0]).items.unshift({ id: "info", title: "Show Details", icon: "info" });

    // add share section to the very end of the menu because the first two sections are dynamic
    if (window.isSecureContext && !!navigator.clipboard) {
      items.push({
        id: "share-section",
        items: [
          {
            id: "share",
            title: "Copy Link to Clipboard",
            icon: "content_copy",
          },
        ],
      });
    }

    return (
      <MenuTriggerButton
        icon={"more_vert"}
        size={TriggerButtonSize.Small}
        label={""}
        onAction={(key) => {
          switch (String(key)) {
            case "open-genome-viewer":
              setProjectFilesForGenomeViewer(dispatch(getProjectFilesDataForSendingToGenomeViewer([file.id])));
              break;
            case "open-data-viewer":
              navigate(`/visualizer/data/dataset/${file.id}`);
              break;
            case "open-analyst":
              void dispatch(
                downloadJnlpFile({
                  moduleId: EXT_APP_MODULES.ANALYST.id,
                  fileIds: [file.id],
                }),
              );
              break;
            case "open-sequence-analyzer":
              void dispatch(forwardToAnalyzer(null, file.id, ENTITY_TYPE.PROJECT_FILE, ANALYZERS.SEQUENCE_ANALYZER));
              break;
            case "share":
              navigator.clipboard
                .writeText(BrowserTab.createUrl(`/content/files?file=${file.id}`))
                .then((_r) => notify.info({ title: "Link Copied!" }, { autoClose: 500 }));
              break;
            case "download":
              void checkProjectFileExists(props.file.id).then((exists) => {
                if (exists) {
                  downloadProjectFile(props.file);
                } else {
                  notify.warning(
                    {
                      title: selMessage({
                        id: "file.download.failed.title",
                      }),
                      children: selMessage({
                        id: "file.download.failed.children",
                        values: { fileNames: props.file.fileName, filesCount: 1 },
                      }),
                    },
                    { autoClose: 5000 },
                  );
                }
              });
              break;
            case "preview":
              setPreviewOpened(true);
              break;
            case "info":
              setInfoOpened(true);
              break;
          }
        }}
        disabledKeys={disabledMenuEntries}
        items={items}
      >
        {(section) => (
          <Section key={section.id} items={section.items}>
            {(item) => (
              <Item key={item.id}>
                <MenuOptionItem {...item} />
              </Item>
            )}
          </Section>
        )}
      </MenuTriggerButton>
    );
  }, [
    genomeViewerMenuItemDisabledHint,
    file,
    configuration,
    props.noPageChangeActions,
    props.file,
    dispatch,
    navigate,
  ]);

  return (
    <>
      <Tile
        forceExternal={props.forceExternal}
        title={file.fileName ?? ""}
        icon={
          !props.hideIcon ? (
            <TileIcon
              filename={file.fileName}
              isFolder={isFolder}
              onPress={
                file.previewable
                  ? () => {
                      setPreviewOpened(true);
                    }
                  : undefined
              }
              lightweight={props.lightweight}
            />
          ) : undefined
        }
        type={`${type ?? ""}${type && fileType ? " — " : ""}${fileType}`}
        statusDisplayName={
          isFolder ? FILE_DISPLAY_NAME[FILE_BROWSE_TYPES.DIRECTORY] : FILE_DISPLAY_NAME[FILE_BROWSE_TYPES.REGULAR_FILE]
        }
        dates={{
          created: file.created ?? undefined,
          modified: file.lastModified ?? undefined,
        }}
        users={{
          creator: file.creator ?? undefined,
          modifier: file.lastModifier ?? undefined,
        }}
        project={file.project ?? undefined}
        to={props.noPageChangeActions ? undefined : `/content/files?file=${file.id}`}
        subtitle={
          props.lightweight
            ? undefined
            : file.humanReadablePath
              ? (getParentPath(file.humanReadablePath) ?? undefined)
              : undefined
        }
        protectionStatus={
          file.deleted ? TileStatus.Deleted : file.hidden ? TileStatus.RestrictedVisibility : TileStatus.Normal
        }
        onPress={props.onPress}
        tooltip={props.tooltip}
        footNote={props.footNote}
        menuButton={!file.hidden && !file.deleted && !isFolder && Menu}
        ref={ref}
        style={props.style}
        lightweight={props.lightweight}
      >
        {file.description}
      </Tile>
      {projectFilesForGenomeViewer && (
        <BamFileValidationDialog
          key="file-bam-validation-dialog"
          projectFiles={projectFilesForGenomeViewer}
          onClose={() => {
            setProjectFilesForGenomeViewer(null);
          }}
        />
      )}
      <FilePreviewControlledDialog
        file={file}
        onCancel={() => {
          setPreviewOpened(false);
        }}
        isOpen={previewOpened}
      />

      <FileDetailsDialog
        isOpen={infoOpened}
        onClose={() => {
          setInfoOpened(false);
        }}
        file={file}
      />
    </>
  );
});

interface TileIconProps {
  filename?: string | null;
  isFolder?: boolean;
  onPress?: () => void;
  lightweight?: boolean;
}

const TileIcon: FC<TileIconProps> = (props) => {
  const { pressProps } = usePress({
    onPress: props.onPress,
  });

  return props.onPress ? (
    <div role="button" {...pressProps} style={{ cursor: "zoom-in" }}>
      <FileIcon filename={props.filename} size={props.lightweight ? "small" : "big"} isFolder={props.isFolder} />
    </div>
  ) : (
    <FileIcon filename={props.filename} size={props.lightweight ? "small" : "big"} isFolder={props.isFolder} />
  );
};
